import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import SalesTransactionsAPI from 'dashboard/api/salesTransactions';
import { useSalesReceipt } from './useSalesReceipt';

const PDF_CONTENT_TYPE = 'application/pdf';

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const transactionProfile = transaction => transaction?.profile || {};

export function useSalesDeliveryStart({ id, t, notify }) {
  const transaction = ref(null);
  const receiptType = ref('manual');
  const loading = ref(false);
  const savingType = ref(false);
  const loadError = ref(false);
  const receipt = useSalesReceipt({
    id,
    t,
    notify,
    receipt: computed(() => transaction.value?.receipt),
  });

  const canEdit = computed(() =>
    Boolean(transaction.value?.permissions?.can_save_draft)
  );
  const isForbidden = computed(() => transaction.value && !canEdit.value);
  const canContinue = computed(
    () => canEdit.value && Boolean(receipt.document.value) && !savingType.value
  );

  const load = async () => {
    if (!id.value) return null;
    loading.value = true;
    loadError.value = false;
    try {
      const response = await SalesTransactionsAPI.show(id.value);
      transaction.value = response?.data || null;
      const profile = transactionProfile(transaction.value);
      receiptType.value =
        profile.recibo_type ||
        transaction.value?.receipt?.signature_type ||
        'manual';
      return transaction.value;
    } catch (error) {
      loadError.value = true;
      notify(errorMessage(error, t('SALES.DELIVERY.START.ERRORS.LOAD')));
      return null;
    } finally {
      loading.value = false;
    }
  };

  const selectReceiptType = async value => {
    if (!canEdit.value || !['manual', 'docusign'].includes(value)) return null;
    if (receiptType.value === value) return transaction.value;

    const previous = receiptType.value;
    receiptType.value = value;
    savingType.value = true;
    try {
      const response = await SalesTransactionsAPI.saveDraft(id.value, {
        recibo_type: value,
      });
      const saved = response?.data;
      if (saved) {
        transaction.value = {
          ...transaction.value,
          ...saved,
          profile: {
            ...transactionProfile(transaction.value),
            ...transactionProfile(saved),
          },
        };
      }
      notify(t('SALES.DELIVERY.START.ALERTS.TYPE_SAVED'));
      return transaction.value;
    } catch (error) {
      receiptType.value = previous;
      notify(errorMessage(error, t('SALES.DELIVERY.START.ERRORS.TYPE')));
      return null;
    } finally {
      savingType.value = false;
    }
  };

  const uploadReceipt = async file => {
    if (!canEdit.value || !file) return null;
    const isPdf =
      file.type === PDF_CONTENT_TYPE || /\.pdf$/i.test(file.name || '');
    if (!isPdf) {
      notify(t('SALES.DELIVERY.START.ERRORS.PDF_ONLY'));
      return null;
    }
    return receipt.upload(file);
  };

  const removeReceipt = () => receipt.remove();

  if (getCurrentInstance()) onMounted(load);

  return {
    canContinue,
    canEdit,
    isForbidden,
    load,
    loadError,
    loading,
    receipt,
    receiptType,
    removeReceipt,
    savingType,
    selectReceiptType,
    transaction,
    uploadReceipt,
  };
}
